import { useState } from "react";
import axios from "axios";
import { Mail, MapPin, Clock } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { Reveal } from "../components/site/Reveal";
import { PrimaryButton } from "../components/site/Buttons";
import { API } from "../lib/site";
import { Seo } from "../components/site/Seo";
import { trackEvent } from "../lib/analytics";
import { toast } from "sonner";

const INFO = [
  { Icon: Mail, title: "Email", text: "info@multiplestreamteam.com", link: "mailto:info@multiplestreamteam.com" },
  { Icon: MapPin, title: "Network", text: "A private network serving members across multiple industries." },
  { Icon: Clock, title: "Response Time", text: "Our team typically responds within a few business days." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", confirm_company: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      trackEvent("contact_form_submit");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <Seo
        title="Contact | MST — Multiple Stream Team"
        description="Questions about MST or the membership process? Contact the MST team."
        path="/contact"
      />
      <PageHero label="Contact" title={<>Get in Touch <span className="text-[#009BFF]">With MST</span></>} text="Questions about MST or the membership process? Send us a message." />
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-16">
          <Reveal className="lg:col-span-2 space-y-10">
            {INFO.map(({ Icon, title, text, link }) => (
              <div key={title} className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#006FD6]/10 flex items-center justify-center shrink-0">
                  <Icon size={20} strokeWidth={1.5} className="text-[#006FD6]" />
                </div>
                <div>
                  <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#041525]" style={{ fontFamily: "var(--font-heading)" }}>{title}</h3>
                  {link ? (
                    <a href={link} data-testid="contact-public-email" className="mt-1.5 inline-block text-sm font-semibold text-[#006FD6] hover:text-[#009BFF] transition-colors duration-300">{text}</a>
                  ) : (
                    <p className="mt-1.5 text-sm text-[#6B7280] leading-relaxed">{text}</p>
                  )}
                </div>
              </div>
            ))}
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-3">
            {sent ? (
              <div data-testid="contact-success" className="rounded-2xl border border-[#006FD6]/20 bg-[#F6F8FA] p-12 text-center">
                <h3 className="text-2xl font-extrabold text-[#041525]" style={{ fontFamily: "var(--font-heading)" }}>Message Sent</h3>
                <p className="mt-4 text-[#6B7280]">Thank you for contacting MST. Our team will get back to you shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl border border-[#062541]/10 bg-[#F6F8FA] p-8 sm:p-10 space-y-6">
                <input
                  type="text"
                  name="confirm_company"
                  value={form.confirm_company}
                  onChange={set("confirm_company")}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] top-auto h-px w-px opacity-0"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="mst-label">Name *</label>
                    <input data-testid="contact-name-input" required className="mst-input" value={form.name} onChange={set("name")} placeholder="Your full name" />
                  </div>
                  <div>
                    <label className="mst-label">Email *</label>
                    <input data-testid="contact-email-input" required type="email" className="mst-input" value={form.email} onChange={set("email")} placeholder="you@company.com" />
                  </div>
                </div>
                <div>
                  <label className="mst-label">Subject</label>
                  <input data-testid="contact-subject-input" className="mst-input" value={form.subject} onChange={set("subject")} placeholder="How can we help?" />
                </div>
                <div>
                  <label className="mst-label">Message *</label>
                  <textarea data-testid="contact-message-input" required rows={6} className="mst-input resize-none" value={form.message} onChange={set("message")} placeholder="Write your message..." />
                </div>
                <PrimaryButton data-testid="contact-submit-btn" type="submit" disabled={sending} className="w-full sm:w-auto disabled:opacity-60">
                  {sending ? "Sending..." : "Send Message"}
                </PrimaryButton>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
